import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { IconTrash, IconFileTypePdf } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { UploadedFile, FileStatus } from './types';
import { tss } from 'tss-react/mui';

const useStyles = tss.create(() => ({
  fileItem: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#FAFBFC',
    borderRadius: '6px',
    border: '1px solid #EAEFF4',
  },
  fileInfo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  fileDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  fileName: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    color: '#2A3547',
  },
  fileSize: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '10px',
    lineHeight: '14px',
    color: '#7C8FAC',
  },
  fileActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  removeButton: {
    color: '#FA896B',
    padding: '4px',
    '&:hover': {
      background: '#FDEDE8',
    },
  },
}));

interface FileListItemCompactProps {
  file: UploadedFile;
  onRemove: (id: string) => void;
}

const FileListItemCompact: React.FC<FileListItemCompactProps> = ({ file, onRemove }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const getStatusColor = (status: FileStatus): string => {
    switch (status) {
      case FileStatus.PROCESSING:
        return '#FFAE1F';
      case FileStatus.PARSING:
        return '#539BFF';
      case FileStatus.PROCESSED:
        return '#13DEB9';
      default:
        return '#7C8FAC';
    }
  };

  const getStatusLabel = (status: FileStatus): string => {
    switch (status) {
      case FileStatus.PROCESSING:
        return t('agentPage.knowledgeBase.status.processing');
      case FileStatus.PARSING:
        return t('agentPage.knowledgeBase.status.parsing');
      case FileStatus.PROCESSED:
        return t('agentPage.knowledgeBase.status.processed');
      default:
        return status;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleRemoveClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmRemove = () => {
    onRemove(file.id);
    setConfirmOpen(false);
  };

  const handleCancelRemove = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Box className={classes.fileItem}>
        <Box className={classes.fileInfo}>
          <IconFileTypePdf size={20} color="#FA896B" />
          <Box className={classes.fileDetails}>
            <Typography className={classes.fileName}>{file.name}</Typography>
            <Typography className={classes.fileSize}>{formatFileSize(file.size)}</Typography>
          </Box>
        </Box>
        <Box className={classes.fileActions}>
          <Chip
            label={getStatusLabel(file.status)}
            size="small"
            sx={{
              backgroundColor: getStatusColor(file.status),
              color: '#ffffff',
              fontWeight: 500,
              fontSize: '11px',
              height: '20px',
            }}
          />
          {file.status === FileStatus.PROCESSED && (
            <IconButton onClick={handleRemoveClick} size="small" className={classes.removeButton}>
              <IconTrash size={16} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={handleCancelRemove}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove &quot;{file.name}&quot;?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileListItemCompact;
